/**
 * FI Task Queue Hooks (Sprint 171 D-002)
 *
 * React Query hooks for the institutional FI task queue. Tasks are derived from
 * pledges awaiting FI officer or FI ops action. Backend routes live at
 * /api/admin/tasks (see services/platform-admin-api).
 *
 * Types derived from platform-admin.openapi.yaml via @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tasksApi,
  type Task,
  type TaskListResponse,
  type TaskCountResponse,
  type BulkTaskActionRequest,
  type BulkTaskReturnRequest,
  type BulkTaskActionResponse,
  type ReassignTaskRequest,
  type TaskListQueryOptions,
} from '../api/services/tasks';

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const TASKS_ROOT = 'admin';
const TASKS_KEY = 'tasks';

const taskKeys = {
  all: [TASKS_ROOT, TASKS_KEY] as const,
  list: (params?: TaskListQueryOptions) =>
    [TASKS_ROOT, TASKS_KEY, 'list', params] as const,
  count: () => [TASKS_ROOT, TASKS_KEY, 'count'] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook for listing FI tasks with optional filtering and cursor pagination.
 */
export function useTasks(params?: TaskListQueryOptions) {
  return useQuery<TaskListResponse, Error>({
    queryKey: taskKeys.list(params),
    queryFn: () => tasksApi.list(params),
  });
}

/**
 * Hook for reading the FI task summary counts (pending, overSla, byAgeBand).
 * Polls every 60 seconds by default so the MetricCards stay warm.
 *
 * Pass `enabled: false` to suppress the fetch entirely (e.g. for roles that
 * do not surface the FI Tasks nav entry).
 */
export function useTaskCount(options?: {
  refetchIntervalMs?: number;
  enabled?: boolean;
}) {
  const refetchInterval = options?.refetchIntervalMs ?? 60_000;
  const enabled = options?.enabled ?? true;
  return useQuery<TaskCountResponse, Error>({
    queryKey: taskKeys.count(),
    queryFn: () => tasksApi.count(),
    refetchInterval,
    enabled,
  });
}

/**
 * Mutation hook for bulk-acknowledging pending tasks. Returns the full
 * BulkTaskActionResponse so the UI can surface per-item failures.
 */
export function useBulkAcknowledgeTasks() {
  const queryClient = useQueryClient();

  return useMutation<BulkTaskActionResponse, Error, BulkTaskActionRequest>({
    mutationFn: (body) => tasksApi.bulkAcknowledge(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/**
 * Mutation hook for bulk-returning pending tasks with a shared reason.
 */
export function useBulkReturnTasks() {
  const queryClient = useQueryClient();

  return useMutation<BulkTaskActionResponse, Error, BulkTaskReturnRequest>({
    mutationFn: (body) => tasksApi.bulkReturn(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/**
 * Mutation hook for reassigning a single task to a different reviewer.
 */
export function useReassignTask() {
  const queryClient = useQueryClient();

  return useMutation<
    Task,
    Error,
    { pledgeId: string; body: ReassignTaskRequest }
  >({
    mutationFn: ({ pledgeId, body }) => tasksApi.reassign(pledgeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
